import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { dateTime, money } from '@/lib/format';
import { riyalsToHalalas } from '@/lib/price-input';
import { useWalletStore } from '@/stores/wallet.store';
import { Choices } from '@/components/fields';
import { Section } from '@/components/page';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui';

/**
 * A customer's store credit (ADR-081): what they can spend, every movement
 * with the balance after it, and — for whoever may refund — a change by hand,
 * always with a reason that stays on the ledger.
 */
export function WalletPanel({ customerId, canAdjust }: { customerId: string; canAdjust: boolean }) {
  const { wallet, entries, nextCursor, loading, error, load, loadMore } = useWalletStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    void load(customerId);
  }, [customerId, load]);

  const currency = wallet?.currency;

  return (
    <Section
      title={t('storeCredit')}
      subtitle={t('storeCreditHint')}
      action={
        canAdjust && !adjusting ? (
          <Button variant="secondary" size="sm" onClick={() => setAdjusting(true)}>
            {t('walletAdjust')}
          </Button>
        ) : undefined
      }
      flush
    >
      <div className="border-t border-line px-5 py-4 text-sm">
        <p data-numeric className="text-2xl font-semibold text-ink">
          {wallet ? money(wallet.balance, locale, currency) : '—'}
        </p>
        {error && !adjusting ? (
          <div className="mt-3">
            <Alert>{error}</Alert>
          </div>
        ) : null}
        {adjusting ? <AdjustForm customerId={customerId} onDone={() => setAdjusting(false)} /> : null}
      </div>
      {entries.length > 0 ? (
        <ul className="divide-y divide-line border-t border-line">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-start justify-between gap-3 px-5 py-2.5 text-sm">
              <span className="min-w-0">
                <span className="block text-ink">{t(`walletKind_${entry.kind}` as MessageKey)}</span>
                <span className="block text-xs text-ink-muted">
                  {dateTime(entry.createdAt, locale)}
                  {entry.order ? (
                    <>
                      {' · '}
                      <Link to={`/orders/${entry.order.id}`} dir="ltr" className="text-brand-700 hover:underline">
                        {entry.order.number}
                      </Link>
                    </>
                  ) : null}
                </span>
                {entry.note ? <span className="block text-xs text-ink-muted">{entry.note}</span> : null}
              </span>
              <span className="shrink-0 text-end">
                <span data-numeric dir="ltr" className={entry.amount > 0 ? 'block font-medium text-success' : 'block font-medium text-ink'}>
                  {entry.amount > 0 ? '+' : '−'}
                  {money(Math.abs(entry.amount), locale, currency)}
                </span>
                <span data-numeric className="block text-xs text-ink-muted">
                  {money(entry.balanceAfter, locale, currency)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : !loading ? (
        <p className="border-t border-line px-5 py-3 text-sm text-ink-muted">{t('walletNoMovements')}</p>
      ) : null}
      {nextCursor ? (
        <div className="border-t border-line px-5 py-2.5">
          <Button variant="ghost" size="sm" disabled={loading} onClick={() => void loadMore()}>
            {t('loadMore')}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

function AdjustForm({ customerId, onDone }: { customerId: string; onDone: () => void }) {
  const { adjust, saving, error } = useWalletStore();
  const t = useT();
  const [direction, setDirection] = useState<'add' | 'take'>('add');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const halalas = riyalsToHalalas(amount);
  const ready = halalas !== null && halalas > 0 && note.trim() !== '';

  // A button, not a form: the panel sits inside the customer page's own form.
  async function submit() {
    if (!ready || halalas === null) return;
    if (await adjust(customerId, direction === 'add' ? halalas : -halalas, note.trim())) onDone();
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      <Choices
        legend={t('walletAdjust')}
        legendHidden
        name="wallet-direction"
        columns={2}
        value={direction}
        onChange={setDirection}
        options={[
          { value: 'add', label: t('walletAdd') },
          { value: 'take', label: t('walletTake') },
        ]}
      />
      <Field label={t('refundAmount')} htmlFor="wallet-amount">
        <Input id="wallet-amount" dir="ltr" inputMode="decimal" autoComplete="off" value={amount} onChange={(event) => setAmount(event.target.value)} />
      </Field>
      <Field label={t('refundWhy')} htmlFor="wallet-note" hint={t('walletNoteHint')}>
        <Textarea id="wallet-note" rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
      {error ? <Alert>{error}</Alert> : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onDone} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button size="sm" disabled={!ready || saving} onClick={() => void submit()}>
          {t('save')}
        </Button>
      </div>
    </div>
  );
}
